import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { env } from 'next-runtime-env';

import { GTMAnalytics } from '~services/analytics';
import { authApi } from '~services/api/auth';
import { eventsApi } from '~services/api/events';
import { shardsApi } from '~services/api/shards';
import type { TEvent } from '~shared/services/eventsInbox';
import { eventsInbox } from '~shared/services/eventsInbox';
import ClientLogger from '~shared/services/logger/ClientLogger';
import { isTestUserEmail } from '~utils/test-user';

import type { RootState } from '../index';
export const clientInit = createListenerMiddleware();

// when on client - start global init
clientInit.startListening({
	matcher: isAnyOf(
		authApi.endpoints.authenticate.matchFulfilled,
		authApi.endpoints.login.matchFulfilled,
		authApi.endpoints.googleLogin.matchFulfilled
	),
	effect: (action, listenerApi) => {
		if (typeof window === 'undefined') {
			return;
		}
		const {
			auth: {
				info: { id, email },
			},
		} = listenerApi.getState() as RootState;

		ClientLogger.updateUserId(id, isTestUserEmail(email));

		const wsUrlFetcher = async (): Promise<string> => {
			const { data } = await listenerApi.dispatch(
				shardsApi.endpoints.getShard.initiate({ id }, { forceRefetch: true })
			);
			if (!data) return '';

			const { shard, key } = data;
			const apiUrl = env('NEXT_PUBLIC_PUBLIC_API') ?? '';
			const base = apiUrl.replace('https', 'wss').replace('api', `rt${shard}`);

			return `${base.endsWith('/') ? base : `${base}/`}${key}`;
		};

		const fallbackFn = async (sync?: string): Promise<TEvent[]> => {
			const { data } = await listenerApi.dispatch(
				eventsApi.endpoints.getEvents.initiate(
					{ id, sync },
					{ forceRefetch: true }
				)
			);
			return data || [];
		};

		eventsInbox.close();
		void eventsInbox.connect(wsUrlFetcher, fallbackFn);
	},
});

clientInit.startListening({
	matcher: authApi.endpoints.authenticate.matchFulfilled,
	effect: (action, listenerApi) => {
		if (typeof window === 'undefined') {
			return;
		}

		const {
			auth: {
				info: { email, id, roles },
			},
		} = listenerApi.getState() as RootState;
		GTMAnalytics.setUserId(id);
		if (!roles.includes('demo')) {
			GTMAnalytics.setEmail(email);
			GTMAnalytics.userAuthorized();
		}
	},
});

clientInit.startListening({
	matcher: isAnyOf(
		authApi.endpoints.login.matchFulfilled,
		authApi.endpoints.googleLogin.matchFulfilled,
		authApi.endpoints.register.matchFulfilled,
		authApi.endpoints.registerGoogle.matchFulfilled
	),
	effect: (action, listenerApi) => {
		if (typeof window === 'undefined') {
			return;
		}
		const {
			auth: {
				info: { email, id },
			},
		} = listenerApi.getState() as RootState;

		GTMAnalytics.setUserId(id);
		GTMAnalytics.setEmail(email);
		GTMAnalytics.userAuthorized();
	},
});
